import { Component } from '@theme/component';

/**
 * "You may also like" (HANDOFF-2 §3.8). The section renders its manual product list; when
 * the section scrolls near the viewport this fetches Shopify's product recommendations
 * rendered by the same section and swaps in those cards if there are any. No
 * recommendations, or a failed request, keeps the manual cards.
 *
 * @typedef {{ grid: HTMLUListElement }} Refs
 * @extends {Component<Refs>}
 */
class DcRelatedComponent extends Component {
  requiredRefs = ['grid'];

  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();
    if (!this.dataset.url || this.dataset.loaded === 'true') return;
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    // A hidden section (no manual cards) has no box to observe; load it right away.
    if (this.hidden) this.#load();
    else this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  async #load() {
    const url = this.dataset.url;
    if (!url) return;
    this.#controller?.abort();
    this.#controller = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#controller.signal });
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = html.querySelector('dc-related-component [ref="grid"]');
      if (!(grid instanceof HTMLElement) || Number(grid.dataset.count) === 0) return;

      this.refs.grid.replaceChildren(...grid.children);
      this.refs.grid.dataset.count = grid.dataset.count ?? '';
      this.dataset.loaded = 'true';
      this.hidden = false;
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.warn('[dc-related] recommendations unavailable', error);
      }
    }
  }
}

if (!customElements.get('dc-related-component')) {
  customElements.define('dc-related-component', DcRelatedComponent);
}
